/**
 * RecycleBinDrawer · AI 智能分类回收站抽屉
 *
 * 列出当前 group 已软删除的根 folder（按 deleted_at desc 分页），支持单项恢复。
 * 与 LLM 完全无关 — 直接 fetch BFF /web/folder/deleted/list + /web/folder/restore。
 *
 * 安全：BFF 端已加 admin 校验 + audit logger，本组件无须再做权限判定，非 admin
 * 用户拉接口会拿到 status=-203，统一渲染为"无权限"提示。
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Drawer, Button, Spin, Empty, message, Modal } from 'antd'
import { FolderOutlined, RestOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { bumpFolderRefreshTick } from '../../../../slices/aiClassify'
import {
  fetchDeletedFolderList,
  restoreFolder
} from '../../services/recycleBin'
import type { DeletedFolderItem } from '../../services/recycleBin'
import styles from './RecycleBinDrawer.module.scss'

interface Props {
  visible: boolean
  onClose: () => void
}

const PAGE_SIZE = 50

const RecycleBinDrawer: React.FC<Props> = ({ visible, onClose }) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s: any) => s.group.groupId)
  const [items, setItems] = useState<DeletedFolderItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  // loading=首页加载（全屏占位）；loadingMore=滚动翻页（列表保留，底部小 spinner）
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoringIDs, setRestoringIDs] = useState<Set<number>>(new Set())

  // 是否还有下一页 · 恢复操作会同步减 items/total，此判定仍成立
  const hasMore = items.length < total

  const load = useCallback(
    async (p: number) => {
      if (!groupID) return
      const isFirstPage = p === 1
      if (isFirstPage) {
        setLoading(true)
        setError(null)
      } else {
        setLoadingMore(true)
      }
      try {
        const { items: list, total: t } = await fetchDeletedFolderList(
          groupID,
          p,
          PAGE_SIZE
        )
        // 首页替换，翻页追加
        setItems((prev) => (isFirstPage ? list : [...prev, ...list]))
        setTotal(t)
        setPage(p)
      } catch (e) {
        if (isFirstPage) {
          setError((e as Error).message || '加载失败')
        } else {
          // 翻页失败不打断已有列表，toast 后继续滚动可重试
          message.error('加载更多失败，请重试')
        }
      } finally {
        if (isFirstPage) setLoading(false)
        else setLoadingMore(false)
      }
    },
    [groupID]
  )

  /** 滚动翻页 · 距底 80px 内且无在途请求且还有下一页时拉 page+1 */
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (loading || loadingMore || !hasMore) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        void load(page + 1)
      }
    },
    [loading, loadingMore, hasMore, page, load]
  )

  // 抽屉每次打开重新拉一次（避免缓存的旧列表）
  useEffect(() => {
    if (visible) {
      void load(1)
    }
  }, [visible, load])

  const handleRestore = (item: DeletedFolderItem): void => {
    if (!groupID) return
    Modal.confirm({
      title: `恢复「${item.folderName}」？`,
      content: '会同时恢复其原有的子文件夹和归档规则。',
      okText: '恢复',
      cancelText: '取消',
      onOk: async () => {
        setRestoringIDs((prev) => {
          const next = new Set(prev)
          next.add(item.folderID)
          return next
        })
        try {
          const resp = await restoreFolder(
            groupID,
            item.folderID,
            'recycle_bin'
          )
          if (resp && (resp.status === 0 || resp.status === undefined)) {
            message.success(`已恢复「${item.folderName}」`)
            // 列表里把这一项摘掉，避免重复点
            setItems((prev) => prev.filter((x) => x.folderID !== item.folderID))
            setTotal((prev) => Math.max(0, prev - 1))
            // 触发左侧 FolderList 重拉
            dispatch(bumpFolderRefreshTick({ preserveRecentlyCreated: true }))
          } else {
            message.error(
              resp?.status === -203
                ? '没有权限，请联系管理员'
                : `恢复失败 status=${resp?.status}`
            )
          }
        } catch (e) {
          message.error(`恢复失败：${(e as Error).message || '网络异常'}`)
        } finally {
          setRestoringIDs((prev) => {
            const next = new Set(prev)
            next.delete(item.folderID)
            return next
          })
        }
      }
    })
  }

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={420}
      title={
        <span className={styles.title}>
          <RestOutlined style={{ marginRight: 8 }} />
          回收站
          <span className={styles.totalHint}>共 {total} 项</span>
        </span>
      }
      destroyOnHidden
      // 高度钉死为 header(55px) 之外的剩余区，溢出交给内部 scrollArea ——
      // 否则 antd Drawer 整个 wrapper-body 跟着内容滚（标题被滚走）
      styles={{
        body: {
          padding: 0,
          height: 'calc(100% - 55px)',
          overflow: 'hidden'
        }
      }}
    >
      {/* 唯一滚动容器 · 滚动翻页监听挂在这里 */}
      <div className={styles.scrollArea} onScroll={handleScroll}>
        {loading && (
          <div className={styles.stateBox}>
            <Spin tip="加载中..." />
          </div>
        )}
        {!loading && error && (
          <div className={styles.stateBox}>
            <div className={styles.errorText}>
              <span>{error}</span>
              <Button onClick={() => load(page)}>重试</Button>
            </div>
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className={styles.stateBox}>
            <Empty description="回收站为空" />
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <>
            <ul className={styles.list}>
              {items.map((it) => (
                <li key={it.folderID} className={styles.row}>
                  <FolderOutlined
                    style={{ fontSize: 22, color: '#94a3b8', marginRight: 10 }}
                  />
                  <div className={styles.meta}>
                    <div className={styles.name}>{it.folderName}</div>
                    <div className={styles.sub}>
                      {it.photoCount} 张 ·{' '}
                      {it.deletedAtUnix
                        ? new Date(it.deletedAtUnix * 1000).toLocaleString(
                            'zh-CN'
                          )
                        : ''}{' '}
                      删除
                    </div>
                  </div>
                  <Button
                    size="small"
                    type="primary"
                    ghost
                    loading={restoringIDs.has(it.folderID)}
                    onClick={() => handleRestore(it)}
                  >
                    恢复
                  </Button>
                </li>
              ))}
            </ul>
            {loadingMore && (
              <div className={styles.loadMore}>
                <Spin size="small" />
              </div>
            )}
            {!loadingMore && !hasMore && (
              <div className={styles.noMore}>没有更多了</div>
            )}
          </>
        )}
      </div>
    </Drawer>
  )
}

export default RecycleBinDrawer
